import { useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useSiteDataLayerEnabled } from '~/hooks/useSiteDataLayerEnabled';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { SITE_DATA_LAYER_IDS, shouldRenderSiteDataLayer } from '~/store2/siteDataLayers';
import { filterGeoItemsByReportType, useListGeoItems } from '../potholing/common';
import { setProject } from '../potholing/openItemStore';
import { PotholeDialogHost, PotholeMapLayers } from '../potholing/PotholeMapLayers';

/**
 * Project-scoped "Site Data → Potholes" map layer. Mounted in Surface (independent of the
 * active tab); renders the shared {@link PotholeMapLayers} when the layer is toggled on,
 * EXCEPT on the Potholing tab itself, whose own MapChildren already renders the same
 * `potholing` source (avoids a duplicate source id). Click → the standard edit dialog; no
 * side-list (that's tab-only). Mirrors the org's report type like the tab does.
 *
 * Waits for {@link rootStore.currentOrganization} so we don't treat a still-loading org
 * (mobileReportType undefined) as potholeLog and flash empty for manhole orgs.
 *
 * `interactive={false}` draws the dots only: no dialog host, so nothing a click could open.
 */
export default function PotholesLayer({ projectId, interactive = true }: { projectId: string; interactive?: boolean }) {
  const { tabId } = useParams({ strict: false }) as { tabId?: string };
  const { visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;
  const org = useSnapshot(rootStore).currentOrganization;
  const siteDataLayerEnabled = useSiteDataLayerEnabled();

  const active = shouldRenderSiteDataLayer(visibleLayerIds, SITE_DATA_LAYER_IDS.potholes, tabId, siteDataLayerEnabled);

  const { data } = useListGeoItems({ projectId, enabled: active });

  // This layer opens records too, so potholeOpenItemStore must belong to this project.
  useEffect(() => {
    setProject(projectId);
  }, [projectId]);

  if (!active || !org) return null;

  const geoItems = filterGeoItemsByReportType(data ?? [], org.mobileReportType);

  return (
    <>
      <PotholeMapLayers
        geoItems={geoItems}
        mobileReportType={org.mobileReportType}
        surface="layer"
        interactive={interactive}
      />
      {interactive && <PotholeDialogHost geoItems={geoItems} mobileReportType={org.mobileReportType} surface="layer" />}
    </>
  );
}
